import type { Lesson } from '../types';

export const m5: Lesson = {
  id: 'm5',
  number: 5,
  title: 'Damping and derivative intuition',
  summary: 'In this module, you will see why some systems bounce endlessly while others creep into place, and discover how adding a derivative term provides the artificial damping needed for smooth, cushioned stops.',
  objectives: [
    'Recognize underdamped, critically damped and overdamped responses.',
    'Compute ζ and ω_n and state when they apply.',
    'Explain the derivative term’s force when the vehicle is moving.',
  ],
  controls: ['controller-kind', 'Kp', 'Kd', 'plant', 'damping-analysis', 'contributions'],
  controllerKinds: ['open-loop', 'pid'],
  sections: [
    {
      id: 'm5-release',
      title: 'The spring-release experiment',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'In Module 4, we saw that increasing proportional gain stiffened the controller but caused violent overshoots and oscillations. In aerospace vehicles, unchecked oscillations cause structural fatigue, passenger airsickness, and crashes into obstacles. To design a flight controller that reaches its target quickly without overshooting, engineers must master the physics of damping and natural oscillation.',
        },
        {
          type: 'p',
          text: "Consider an experiment where a tethered vehicle is pulled 0.25 m above its hover altitude and released from rest with only hover thrust ($T_0$) active. The spring pulls downward toward rest, while aerodynamic damping resists motion. Depending on the strength of damping, the vehicle returns to the hover altitude in one of three characteristic ways.",
        },
        {
          type: 'p',
          text: 'Two fundamental parameters govern how momentum, spring tension, and drag interact during release:',
        },
        {
          type: 'list',
          items: [
            '**Natural angular frequency** ($\\term{wn}{\\omega_n}$): The frequency at which the system would oscillate if there were zero damping (measured in radians per second).',
            '**Damping ratio** ($\\term{zeta}{\\zeta}$): A dimensionless number that compares the actual damping against the minimum amount needed to prevent all overshoot.',
          ],
        },
        {
          type: 'math',
          tex: '\\term{wn}{\\omega_n} = \\sqrt{\\frac{\\term{k}{k}}{\\term{m}{m}}} \\qquad \\term{zeta}{\\zeta} = \\frac{\\term{b}{b}}{2\\sqrt{\\term{m}{m}\\,\\term{k}{k}}}',
          aria: 'Omega n equals the square root of k over m. Zeta equals b divided by 2 times the square root of m times k.',
        },
        {
          type: 'table',
          caption: 'Damping regimes (for positive stiffness and damping)',
          headers: ['ζ', 'Name', 'Shape'],
          rows: [
            ['0 < ζ < 1', '{{underdamped}}', 'Overshoots the target and rings with decaying sinusoidal oscillations.'],
            ['ζ = 1', '{{critically-damped}}', 'The boundary case: returns to equilibrium as rapidly as possible without oscillating.'],
            ['ζ > 1', '{{overdamped}}', 'No oscillation or overshoot, but heavy drag causes the craft to creep sluggishly back.'],
          ],
        },
        {
          type: 'p',
          text: 'If damping were zero ($b = 0$, so $\\zeta = 0$), the vehicle would oscillate indefinitely as an undamped harmonic oscillator.',
        },
        {
          type: 'callout',
          tone: 'warn',
          text: 'Critical damping ($\\zeta = 1$) provides the fastest non-oscillating return for this initial displacement release. However, it is not an absolute rule for all flight modes. Real flight controllers often accept a tiny amount of underdamping ($\\zeta \\approx 0.7$) to achieve a faster initial rise time.',
        },
        { type: 'widget', widget: 'damping-calculator' },
      ],
    },
    {
      id: 'm5-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'Use the damping ratio formula $\\zeta = \\frac{b}{2\\sqrt{m\\,k}}$ to predict which damping coefficient creates a critically damped return.',
        },
        {
          type: 'predict',
          id: 'm5-p1',
          prompt: 'k = 4 N/m, m = 1 kg. You will try b = 1, 4 and 8 N·s/m. Which returns to 2 m fastest without crossing below it?',
          options: [
            { id: '1', text: 'b = 1' },
            { id: '4', text: 'b = 4' },
            { id: '8', text: 'b = 8' },
          ],
          reveal: 'With $m = 1\\text{ kg}$ and $k = 4\\text{ N/m}$, the denominator is $2\\sqrt{1 \\times 4} = 4$. Thus $\\zeta = b / 4$. Setting $b = 4\\text{ N}\\cdot\\text{s/m}$ gives $\\zeta = 1$ (critical damping). When $b = 1$, $\\zeta = 0.25$ (underdamped, overshoots below 2 m). When $b = 8$, $\\zeta = 2$ (overdamped, sluggish creep).',
        },
      ],
    },
    {
      id: 'm5-lab',
      title: 'Physical damping, then controller damping',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'This lab is divided into two distinct parts that bridge classical mechanical physics and active flight control:',
        },
        {
          type: 'h',
          text: 'Part 1: Passive Physical Damping on a Tethered Plant',
        },
        {
          type: 'p',
          text: '**Purpose**: Build intuition for energy dissipation in an unforced mechanical system before introducing feedback algorithms. Here, the vehicle is attached to a spring ($k = 4\\text{ N/m}$) and released from 0.25 m above equilibrium with open-loop hover thrust ($T_0$) only—no feedback controller is running ($K_p = 0, K_d = 0$).',
        },
        {
          type: 'p',
          text: 'By sweeping the physical damping coefficient $b$ ($1, 4,\\text{ and } 8\\text{ N}\\cdot\\text{s/m}$), you will observe the three fundamental second-order damping regimes governed by $\\zeta = \\frac{b}{2\\sqrt{m\\,k}}$: underdamped bouncing ($\\zeta = 0.25$), critical non-oscillating return ($\\zeta = 1.0$), and overdamped sluggish creep ($\\zeta = 2.0$). This establishes the physical ground truth that flight controllers strive to reproduce.',
        },
        {
          type: 'h',
          text: 'Part 2: Active Controller Damping in Flight Software',
        },
        {
          type: 'p',
          text: '**Purpose**: Real aerial vehicles fly freely through the air with no physical tether spring ($k = 0$) and minimal aerodynamic drag ($b \\approx 0$). We cannot bolt a heavy mechanical shock absorber to a multicopter! Instead, flight control engineers synthesize both stiffness and damping entirely in software.',
        },
        {
          type: 'p',
          text: 'In this part, you switch to the free-altitude drone. Proportional gain $K_p = 16\\text{ N/m}$ provides artificial stiffness (acting as a virtual software spring that pulls toward the target), while derivative gain $K_d$ generates artificial damping by actively braking against vehicle velocity:',
        },
        {
          type: 'math',
          tex: '\\term{ufb}{u_{fb}} = \\term{Kp}{K_p}\\, \\term{error}{e} - \\term{Kd}{K_d}\\, \\term{vest}{v_{est}}',
          aria: 'u f b equals K p times error e, minus K d times the estimated velocity v est.',
        },
        {
          type: 'p',
          text: 'Notice the sign of the derivative term: $-K_d\\,v_{est}$ pushes downward when the vehicle climbs ($v > 0$) and pushes upward when it sinks ($v < 0$). This mirrors physical viscous damping ($-b\\,v$) line for line! Combining physical hardware parameters with software gains gives the unified second-order parameters:',
        },
        {
          type: 'math',
          tex: '\\text{stiffness} = \\term{k}{k} + \\term{Kp}{K_p} \\quad \\text{damping} = \\term{b}{b} + \\term{Kd}{K_d} \\quad \\term{wn}{\\omega_n} = \\sqrt{\\frac{\\term{k}{k} + \\term{Kp}{K_p}}{\\term{m}{m}}} \\quad \\term{zeta}{\\zeta} = \\frac{\\term{b}{b} + \\term{Kd}{K_d}}{2\\sqrt{\\term{m}{m}\\,(\\term{k}{k} + \\term{Kp}{K_p})}}',
          aria: 'Effective stiffness is k plus K p. Effective damping is b plus K d. Omega n is the square root of k plus K p, over m. Zeta is b plus K d, over 2 times the square root of m times the quantity k plus K p.',
        },
        {
          type: 'p',
          text: 'For our free-altitude vehicle ($k = 0, b = 1, m = 1$) with $K_p = 16$, the formula simplifies to $\\zeta = (1 + K_d)/8$. Testing $K_d = 1, 7,$ and $15$ produces $\\zeta = 0.25$ (underdamped), $1.0$ (critical), and $2.0$ (overdamped)—proving that software derivative feedback can cushion stops and eliminate oscillations just as effectively as a physical mechanical damper.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm5-inspect',
      title: 'Inspect',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Compare the physical hardware experiments against the software controller runs:',
        },
        {
          type: 'list',
          items: [
            'Compare the three physical $b$ runs against the three software $K_d$ runs. Verify that identical values of $\\zeta$ produce identical altitude trajectories.',
            'Open **Damping analysis** below the metrics to review the second-order model assumptions.',
            'In a $K_d$ run, expand **Controller contributions**: verify that when the drone drops downward ($v < 0$), the $u_D$ term becomes positive to brake the descent.',
          ],
        },
        {
          type: 'callout',
          tone: 'note',
          text: 'On a spring-free vehicle with $K_p = 0$, stiffness is zero, so natural frequency and damping ratio are undefined. Furthermore, adding Integral action (Module 6) introduces a third state, meaning second-order $\\zeta$ formulas serve as an intuitive guideline rather than an exhaustive stability test.',
        },
      ],
    },
    {
      id: 'm5-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        { type: 'reflect', id: 'm5-r1', prompt: 'How is the derivative term like the damper you drew in the spring experiment? How is it different?' },
      ],
    },
  ],
  lab: {
    id: 'm5-lab',
    version: 1,
    instructions: 'Complete both parts (10 s per run): Steps 1–3 test physical damping ($b$) on the tethered plant; Steps 4–6 test software derivative damping ($K_d$) on the free drone.',
    steps: [
      ...([
        {
          b: 1,
          instructions:
            'Watch the vehicle plunge past 2 m down to ~1.85 m, then bounce and oscillate across 2 m several times (underdamped: $\\zeta = 0.25$) because aerodynamic drag is too weak to quickly absorb the spring’s energy.',
        },
        {
          b: 4,
          instructions:
            'Watch the vehicle glide smoothly down to 2 m and settle in minimum time without ever dropping below target (critically damped: $\\zeta = 1.0$, zero overshoot).',
        },
        {
          b: 8,
          instructions:
            'Notice how strong drag chokes descent speed: the vehicle never overshoots 2 m, but creeps down sluggishly like it is sinking through honey (overdamped: $\\zeta = 2.0$).',
        },
      ] as const).map(({ b, instructions }) => ({
        id: `m5-b${b}`,
        title: `Part 1: Release with $b = ${b}$ N·s/m`,
        presetId: 'damping-release',
        setup: (c: import('../../simulation/types').ExperimentConfig) => {
          c.plant.b = b;
        },
        instructions,
        permitted: [],
        requires: `A complete 10 s run with b = ${b}.`,
      })),
      ...([
        {
          kd: 1,
          instructions:
            'With artificial stiffness $K_p = 16$ but weak derivative damping $K_d = 1$, watch the craft overshoot below 2 m and ring with bouncing oscillations ($\\zeta = 0.25$), recreating the underdamped spring release.',
        },
        {
          kd: 7,
          instructions:
            'As the vehicle descends, the derivative term ($-K_d v_{est}$) pushes upward to brake the motion, cushioning the landing onto 2 m with zero overshoot (critically damped: $\\zeta = 1.0$).',
        },
        {
          kd: 15,
          instructions:
            'Heavy derivative damping overdamps the loop ($\\zeta = 2.0$): the controller brakes so aggressively against downward velocity that the drone creeps down very slowly toward 2 m.',
        },
      ] as const).map(({ kd, instructions }) => ({
        id: `m5-kd${kd}`,
        title: `Part 2: Controller damping $K_d = ${kd}$ N·s/m`,
        presetId: 'pd-release',
        setup: (c: import('../../simulation/types').ExperimentConfig) => {
          c.controller.Kd = kd;
        },
        instructions,
        permitted: [],
        requires: `A complete 10 s run with $K_p$ = 16, $K_i$ = 0, $K_d$ = ${kd}.`,
      })),
    ],
  },
  gate: {
    id: 'g5',
    version: 1,
    title: 'Check: damping',
    questions: [
      {
        id: 'g5-q1',
        version: 1,
        kind: 'match',
        binds: ['m5-b1', 'm5-b4', 'm5-b8'],
        prompt: () => 'These are altitude traces from your three release runs, in shuffled order. Match each to its damping regime.',
        figure: () => ({
          caption: 'Your release runs (altitude, m, versus time, s)',
          series: [
            { label: 'Trace A', stepId: 'm5-b8' },
            { label: 'Trace B', stepId: 'm5-b1' },
            { label: 'Trace C', stepId: 'm5-b4' },
          ],
        }),
        items: [
          { id: 'A', text: 'Trace A' },
          { id: 'B', text: 'Trace B' },
          { id: 'C', text: 'Trace C' },
        ],
        choices: [
          { id: 'under', text: 'underdamped' },
          { id: 'crit', text: 'critically damped' },
          { id: 'over', text: 'overdamped' },
        ],
        correct: () => ({ A: 'over', B: 'under', C: 'crit' }),
        hint: 'Which one crosses below 2 m? Of the other two, which gets back faster?',
        explanation: 'Crossing and ringing means underdamped. Fastest without crossing means critical. Slow creep means overdamped.',
        review: 'm5-release',
      },
      {
        id: 'g5-q2',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'The vehicle is moving upward at 0.3 m/s (measured). With $K_d$ = 7 N·s/m, what force does the derivative term request?',
        options: [
          { id: 'down', text: '2.1 N downward (−2.1 N)' },
          { id: 'up', text: '2.1 N upward (+2.1 N)', feedback: 'The derivative term is $-K_d v_{est}$. It opposes motion.' },
          { id: 'zero', text: 'Zero, if the error is zero', feedback: 'With derivative on measurement, $u_D$ depends on velocity, not on the error.' },
          { id: 'target', text: 'It depends on where the target is', feedback: 'The D term only uses measured velocity.' },
        ],
        correct: () => 'down',
        hint: '$u_D = -K_d\\, v_{est}$.',
        explanation: '−7 × 0.3 = −2.1 N. Controller-made damping opposing the upward motion.',
        review: 'm5-lab',
      },
      {
        id: 'g5-q3',
        version: 1,
        kind: 'numeric',
        binds: [],
        prompt: () => 'Free-altitude vehicle: m = 1 kg, k = 0, b = 1 N·s/m. Controller: $K_p$ = 16 N/m, $K_i$ = 0, $K_d$ = 3 N·s/m. What is ζ?',
        unit: '(unitless)',
        answer: () => 0.5,
        tolerance: 0.01,
        misconceptions: [
          { test: (x) => Math.abs(x - 3 / 8) < 0.01, feedback: 'Include the physical damping. The numerator is b + K_d.' },
          { test: (x) => Math.abs(x - 4 / 32) < 0.01, feedback: 'The denominator is 2√(m(k + K_p)) = 2 × 4 = 8.' },
        ],
        hint: '$\\zeta = (b + K_d)/(2\\sqrt{m(k + K_p)})$.',
        explanation: '(1 + 3)/(2 × 4) = 0.5. Underdamped.',
        review: 'm5-lab',
      },
      {
        id: 'g5-q4',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'Under which assumptions does the displayed damping ratio describe the closed loop?',
        options: [
          { id: 'ok', text: 'Fixed target, constant feedforward, no integral action, ideal derivative, no saturation or ground contact.' },
          { id: 'any', text: 'For any PID controller.', feedback: 'Integral action adds a third state. ζ alone no longer describes the response.' },
          { id: 'spring', text: 'Only when the physical spring is attached.', feedback: '$K_p$ supplies the stiffness instead, as in your $K_d$ runs.' },
          { id: 'sat', text: 'Even when thrust saturates.', feedback: 'Saturation makes the loop nonlinear. The formula assumes the thrust request is always delivered.' },
        ],
        correct: () => 'ok',
        hint: 'The formula came from a linear spring-mass-damper.',
        explanation: 'Outside these assumptions, trust the simulation evidence rather than the label.',
        review: 'm5-inspect',
      },
    ],
  },
};
